import * as L from "leaflet";
import { cssRule } from "typestyle";

// Esri attribution for interactive maps: "Powered by Esri" always visible, the
// data sources behind an (i) button.
// https://developers.arcgis.com/documentation/esri-and-data-attribution/interactive-maps/
// Shown until Esri's own credit of the layer arrives, and when it cannot be loaded
export const ESRI_IMAGERY_SOURCES =
  "Esri, Vantor, GeoEye, Earthstar Geographics, CNES/Airbus DS, USDA, USGS, AeroGRID, IGN, and the GIS User Community";
export const ESRI_LABELS_SOURCES =
  "Esri, TomTom, Garmin, FAO, NOAA, USGS, \u00a9 OpenStreetMap contributors, and the GIS User Community";

// The (i) of the credit: one SVG in every Alpha app, so fonts and button resets cannot change it
const INFO_ICON =
  '<svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false">' +
  '<circle cx="7" cy="7" r="6.25" fill="none" stroke="currentColor" stroke-width="1.25"/>' +
  '<circle cx="7" cy="4.25" r="0.95" fill="currentColor"/>' +
  '<rect x="6.3" y="6" width="1.4" height="4.6" rx="0.7" fill="currentColor"/></svg>';

const escapeHtml = (text: string) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** The credit with one sources line per Esri layer on the map: `Imagery: ...` and, if given, `Labels: ...`. */
export const esriAttribution = (imagery: string, labels?: string) =>
  '<span class="esri-credit-sources" hidden>' +
  `<span class="esri-credit-list">Imagery: ${escapeHtml(imagery)}</span>` +
  (labels === undefined
    ? ""
    : `<span class="esri-credit-list">Labels: ${escapeHtml(labels)}</span>`) +
  "</span>" +
  '<span class="esri-credit-line">' +
  '<a href="https://www.esri.com" target="_blank" rel="noreferrer">Powered by Esri</a>' +
  `<button type="button" class="esri-credit-toggle" aria-label="Map data sources" aria-expanded="false">${INFO_ICON}</button>` +
  "</span>";

// Esri's credit of a layer ("copyrightText" of its service metadata), loaded once per page and service.
// `text` is set once it has loaded.
type EsriCopyright = { text?: string; request: Promise<string | undefined> };
const esriCopyrights = new Map<string, EsriCopyright>();
let esriCopyrightWarned = false;

/** Service metadata URL of an Esri tile URL template: the template without `/tile/{z}/{y}/{x}`, as JSON. */
export const esriMetadataUrl = (tileUrl: string) => {
  const [path, query] = tileUrl.split("?");
  return `${path.replace(/\/tile\/\{z\}\/\{y\}\/\{x\}$/, "")}?f=json${
    query ? `&${query}` : ""
  }`;
};

/** Esri's credit of the tile layer without its "Source:"/"Sources:" prefix; undefined if it cannot be loaded. */
export const loadEsriCopyright = (tileUrl: string): EsriCopyright => {
  const url = esriMetadataUrl(tileUrl);
  const cached = esriCopyrights.get(url);
  if (cached) return cached;

  const copyright: EsriCopyright = {
    request: fetch(url)
      .then((response) =>
        response.ok
          ? response.json()
          : Promise.reject(new Error(`HTTP ${response.status}`)),
      )
      .then(
        (
          metadata: { copyrightText?: unknown; error?: unknown } | null,
        ): string => {
          const text =
            typeof metadata?.copyrightText === "string"
              ? metadata.copyrightText.replace(/^\s*Sources?:\s*/i, "").trim()
              : "";
          if (!text) {
            throw new Error(
              metadata?.error
                ? JSON.stringify(metadata.error)
                : "no copyrightText",
            );
          }
          return (copyright.text = text);
        },
      )
      .catch((error): undefined => {
        // Esri's credit is optional: one warning per page, the built-in sources stay
        if (!esriCopyrightWarned) {
          // eslint-disable-next-line no-console -- a single hint for developers, not an app error
          console.warn("Esri map credit: using the built-in sources", error);
        }
        esriCopyrightWarned = true;
        return undefined;
      }),
  };
  esriCopyrights.set(url, copyright);
  return copyright;
};

/** An Esri tile layer and its tile URL template */
export type EsriLayer = { layer: L.TileLayer; url: string };

/**
 * Puts the Esri credit on the imagery layer: the built-in sources first, Esri's own credit of each layer once
 * loaded. The Labels line shows only while the labels layer is on the map.
 */
export const showEsriCredit = (imagery: EsriLayer, labels?: EsriLayer) => {
  const imageryCopyright = loadEsriCopyright(imagery.url);
  const labelsCopyright = labels && loadEsriCopyright(labels.url);
  let labelsOn = false;

  const render = () => {
    const { options } = imagery.layer;
    const attribution = esriAttribution(
      imageryCopyright.text ?? ESRI_IMAGERY_SOURCES,
      labelsOn ? labelsCopyright?.text ?? ESRI_LABELS_SOURCES : undefined,
    );
    if (attribution === options.attribution) return;
    // The attribution control shows the attribution strings of the layers on the map: swap the old one for the new
    const control = (imagery.layer as unknown as { _map?: L.Map })._map
      ?.attributionControl;
    if (options.attribution) control?.removeAttribution(options.attribution);
    options.attribution = attribution;
    control?.addAttribution(attribution);
  };

  labels?.layer.on("add", () => {
    labelsOn = true;
    render();
  });
  labels?.layer.on("remove", () => {
    labelsOn = false;
    render();
  });
  render();
  void imageryCopyright.request.then(render);
  void labelsCopyright?.request.then(render);
};

// Map credit look shared by all Alpha apps: the attribution control shows the credit of the
// base layer on screen (Esri or OpenStreetMap) in the same box.
cssRule(
  ".leaflet-container .leaflet-control-container .leaflet-control-attribution",
  {
    padding: "1px 6px",
    font: '12px/16px "Avenir Next W00", "Helvetica Neue", Helvetica, Arial, sans-serif',
    color: "#323232",
    background: "rgba(255, 255, 255, 0.65)",
    textAlign: "right",
  },
);
cssRule(
  ".leaflet-container .leaflet-control-container .leaflet-control-attribution a",
  {
    color: "inherit",
    textDecoration: "none",
    $nest: { "&:hover, &:focus": { textDecoration: "underline" } },
  },
);
cssRule(".esri-credit-line", {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  whiteSpace: "nowrap",
  verticalAlign: "top",
});
// In flow above the credit line: opening it grows the control, so controls
// kept clear of the credit stay clear of the panel too.
cssRule(".esri-credit-sources:not([hidden])", {
  display: "block",
  maxWidth: 280,
  padding: "3px 0 2px",
  textAlign: "left",
  whiteSpace: "normal",
  userSelect: "text",
  cursor: "text",
});
cssRule(".esri-credit-list", { display: "block" });
// Self-contained so global and UA button styles cannot change it
cssRule(".esri-credit-toggle", {
  appearance: "none",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flex: "none",
  boxSizing: "border-box",
  width: 14,
  height: 14,
  margin: 0,
  padding: 0,
  border: 0,
  background: "transparent",
  color: "inherit",
  cursor: "pointer",
  verticalAlign: "middle",
  $nest: {
    "&:hover": { opacity: 0.7 },
    "&:focus-visible": { outline: "1px solid currentColor", outlineOffset: 1 },
  },
});

/** Opens/closes the sources panel: (i) toggles; Esc or a map click closes. */
export const bindEsriCredit = (map: L.Map) => {
  const control = map.attributionControl?.getContainer();
  if (!control) return;

  const setOpen = (open: boolean) => {
    const toggle = control.querySelector(".esri-credit-toggle");
    const sources = control.querySelector<HTMLElement>(".esri-credit-sources");
    if (!toggle || !sources) return;
    toggle.setAttribute("aria-expanded", String(open));
    sources.hidden = !open;
  };
  const isOpen = () =>
    control
      .querySelector(".esri-credit-toggle")
      ?.getAttribute("aria-expanded") === "true";

  // The control stops click propagation, so (i) clicks never reach the map.
  L.DomEvent.on(control, "click", (e) => {
    if ((e.target as Element).closest(".esri-credit-toggle")) {
      setOpen(!isOpen());
    }
  });
  map.on("click", () => setOpen(false));

  const closeOnEscape = (e: KeyboardEvent) => {
    if (e.key === "Escape") setOpen(false);
  };
  document.addEventListener("keydown", closeOnEscape);
  map.on("unload", () =>
    document.removeEventListener("keydown", closeOnEscape),
  );
};
